<template>
  <div class="google-page">
    <el-card shadow="never" class="header-card">
      <div class="page-header">
        <div>
          <h2>准备素材</h2>
          <p>请先选择所属 Customer，再按素材类型批量上传。列表默认展示全量数据，包含该账户下已导入的内容。</p>
        </div>
      </div>

      <div class="customer-section">
        <div class="customer-block">
          <div class="section-label">素材所属 Customer</div>
          <div class="customer-cards" v-loading="customerLoading">
            <div
              v-for="item in customers"
              :key="item.customerId"
              class="customer-card"
              :class="{ active: selectedCustomerId === item.customerId }"
              @click="selectedCustomerId = item.customerId"
            >
              <div class="customer-id">{{ item.label }}</div>
              <div v-if="item.webSiteUrl" class="customer-site">{{ item.webSiteUrl }}</div>
            </div>
            <div v-if="!customerLoading && !customers.length" class="empty-customers">
              暂无可用 Customer
            </div>
          </div>
        </div>

        <div class="header-actions">
          <el-button size="small" icon="el-icon-picture" @click="openLogoUpload">
            批量上传徽标
          </el-button>
          <el-button type="primary" size="small" icon="el-icon-video-camera" @click="openVideoUpload">
            批量上传广告视频
          </el-button>
        </div>
      </div>

      <el-alert
        class="upload-tip"
        type="info"
        show-icon
        :closable="false"
        title="可一次选择多个文件。视频和徽标分开上传，避免选错素材类型。重复名称不允许上传。"
      />
    </el-card>

    <el-card shadow="never" class="table-card">
      <div class="table-toolbar">
        <div class="toolbar-left">
          <span class="table-title">素材列表</span>
          <el-select
            v-model="filterCustomerId"
            size="small"
            clearable
            placeholder="全部 Customer"
            style="width: 280px"
          >
            <el-option label="全部 Customer" value="" />
            <el-option
              v-for="item in customers"
              :key="item.customerId"
              :label="customerOptionLabel(item)"
              :value="item.customerId"
            />
          </el-select>
          <el-button size="small" icon="el-icon-search" @click="handleFilter">筛选</el-button>
          <span class="total-text">共 {{ total }} 条</span>
        </div>
        <div class="toolbar-right">
          <el-button
            size="small"
            icon="el-icon-refresh"
            :loading="refreshLoading"
            @click="refreshStatus"
          >
            刷新状态
          </el-button>
          <el-button
            size="small"
            icon="el-icon-download"
            :loading="exportLoading"
            @click="handleExport"
          >
            导出 Excel
          </el-button>
        </div>
      </div>

      <el-table
        v-loading="tableLoading"
        :data="tableData"
        border
        stripe
        size="small"
        style="width: 100%"
      >
        <el-table-column prop="assetId" label="素材 ID" min-width="160" show-overflow-tooltip />
        <el-table-column label="Customer ID" width="150" align="center">
          <template slot-scope="scope">
            {{ formatCustomerId(scope.row.customerId) }}
          </template>
        </el-table-column>
        <el-table-column label="站点" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ getCustomerSite(scope.row.customerId) }}
          </template>
        </el-table-column>
        <el-table-column prop="assetType" label="素材类型" width="110" align="center">
          <template slot-scope="scope">
            {{ mapAssetTypeLabel(scope.row.assetType) }}
          </template>
        </el-table-column>
        <el-table-column prop="assetName" label="视频名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="assetResourceName" label="实际使用名称" min-width="220" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.assetResourceName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="adsName" label="广告系列名称" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.adsName || '未关联' }}
          </template>
        </el-table-column>
        <el-table-column label="上传状态" width="120" align="center">
          <template slot-scope="scope">
            <span class="status-pill" :class="'is-' + mapUploadStatus(scope.row.uploadStatus).type">
              {{ mapUploadStatus(scope.row.uploadStatus).label }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="uploadTime" label="上传时间" width="170" align="center" />
      </el-table>

      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        />
      </div>
    </el-card>

    <el-dialog
      title="批量上传广告视频"
      :visible.sync="videoDialogVisible"
      width="560px"
      @closed="resetVideoDialog"
    >
      <el-form label-width="88px">
        <el-form-item label="Customer">
          <span>{{ selectedCustomerText }}</span>
        </el-form-item>
        <el-form-item label="视频文件">
          <el-upload
            ref="videoUpload"
            action="#"
            :auto-upload="false"
            multiple
            accept=".mp4,.mov,.m4v,.mpeg,video/*"
            :file-list="videoFileList"
            :on-change="handleVideoChange"
            :on-remove="handleVideoRemove"
          >
            <el-button size="small" type="primary">选择视频</el-button>
            <div slot="tip" class="el-upload__tip">支持批量选择，视频名称不可与已有素材重复</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="videoDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="videoUploading" @click="submitVideoUpload">开始上传</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="批量上传徽标"
      :visible.sync="logoDialogVisible"
      width="560px"
      @closed="resetLogoDialog"
    >
      <el-form label-width="88px">
        <el-form-item label="Customer">
          <span>{{ selectedCustomerText }}</span>
        </el-form-item>
        <el-form-item label="徽标文件">
          <el-upload
            ref="logoUpload"
            action="#"
            :auto-upload="false"
            multiple
            accept=".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif"
            :file-list="logoFileList"
            :on-change="handleLogoChange"
            :on-remove="handleLogoRemove"
            :before-upload="() => false"
          >
            <el-button size="small" type="primary">选择徽标</el-button>
            <div slot="tip" class="el-upload__tip">GIF / JPEG / PNG，1:1，最低 128x128，允许 ±1%</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="logoDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="logoUploading" @click="submitLogoUpload">开始上传</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
  import {
    ASSET_TYPE,
    UPLOAD_STATUS,
    customerOptionLabel,
    exportAssets,
    formatCustomerId,
    listAssets,
    listCustomers,
    mapAssetTypeLabel,
    mapUploadStatus,
    uploadAndSubmitAssets
  } from '@/api/googleAds'
  import { downloadExcel } from '@/utils/excel'

  export default {
    name: 'GoogleCreateAsset',
    data() {
      return {
        customers: [],
        customerLoading: false,
        selectedCustomerId: '',
        filterCustomerId: '',
        appliedFilterCustomerId: '',
        tableData: [],
        tableLoading: false,
        currentPage: 1,
        pageSize: 20,
        total: 0,
        refreshLoading: false,
        exportLoading: false,
        videoDialogVisible: false,
        logoDialogVisible: false,
        videoFileList: [],
        logoFileList: [],
        videoUploading: false,
        logoUploading: false,
        pollTimer: null
      }
    },
    mounted() {
      this.initPage()
    },
    beforeDestroy() {
      this.clearPoll()
    },
    computed: {
      selectedCustomerText() {
        const item = this.customers.find((customer) => customer.customerId === this.selectedCustomerId)
        return item ? customerOptionLabel(item) : this.formatCustomerId(this.selectedCustomerId)
      }
    },
    methods: {
      formatCustomerId,
      customerOptionLabel,
      mapAssetTypeLabel,
      mapUploadStatus,
      getCustomerSite(customerId) {
        const item = this.customers.find((customer) => customer.customerId === customerId)
        return (item && item.webSiteUrl) || '-'
      },
      ensureCustomerSelected() {
        if (this.selectedCustomerId) return true
        this.$message.warning('请先选择所属 Customer ID')
        return false
      },
      async initPage() {
        await this.loadCustomers()
        await this.loadData()
      },
      async loadCustomers() {
        this.customerLoading = true
        try {
          const response = await listCustomers()
          this.customers = response.body.data || []
          if (!this.selectedCustomerId && this.customers.length) {
            this.selectedCustomerId = this.customers[0].customerId
          }
        } catch (error) {
          this.$message.error(error.message || '加载 Customer 失败')
        } finally {
          this.customerLoading = false
        }
      },
      async loadData(silent = false) {
        if (!silent) this.tableLoading = true
        try {
          const response = await listAssets({
            customerId: this.appliedFilterCustomerId,
            pageNum: this.currentPage,
            pageSize: this.pageSize
          })
          const list = Array.isArray(response.body && response.body.data)
            ? response.body.data
            : []
          this.total = (response.body && response.body.total) || 0
          if (silent) {
            this.tableData.forEach((row, index) => {
              const fresh = list.find((item) => item.id === row.id)
              if (fresh) this.$set(this.tableData, index, fresh)
            })
          } else {
            this.tableData = list
          }
          this.setupPoll()
        } catch (error) {
          this.$message.error(error.message || '加载素材失败')
        } finally {
          if (!silent) this.tableLoading = false
        }
      },
      handleFilter() {
        this.appliedFilterCustomerId = this.filterCustomerId
        this.currentPage = 1
        this.loadData()
      },
      async refreshStatus() {
        this.refreshLoading = true
        try {
          await this.loadData()
          this.$message.success('状态已刷新')
        } finally {
          this.refreshLoading = false
        }
      },
      openVideoUpload() {
        if (!this.ensureCustomerSelected()) return
        this.videoDialogVisible = true
      },
      openLogoUpload() {
        if (!this.ensureCustomerSelected()) return
        this.logoDialogVisible = true
      },
      resetVideoDialog() {
        this.videoFileList = []
        if (this.$refs.videoUpload) this.$refs.videoUpload.clearFiles()
      },
      resetLogoDialog() {
        this.logoFileList = []
        if (this.$refs.logoUpload) this.$refs.logoUpload.clearFiles()
      },
      handleVideoChange(file, fileList) {
        this.videoFileList = fileList
      },
      handleVideoRemove(file, fileList) {
        this.videoFileList = fileList
      },
      handleLogoChange(file, fileList) {
        this.logoFileList = fileList
      },
      handleLogoRemove(file, fileList) {
        this.logoFileList = fileList
      },
      getRawFiles(fileList) {
        return fileList.map((item) => item.raw).filter(Boolean)
      },
      fileBaseName(name) {
        return String(name || '').replace(/\.[^.]+$/, '').toLowerCase()
      },
      async findDuplicateNames(files, assetType) {
        const selectedNames = files.map((file) => file.name)
        const selectedDup = selectedNames.filter(
          (name, index) =>
            selectedNames.findIndex((item) => this.fileBaseName(item) === this.fileBaseName(name)) !== index
        )
        const response = await listAssets({
          customerId: this.selectedCustomerId,
          pageNum: 1,
          pageSize: 10000
        })
        const existedDup = (Array.isArray(response.body && response.body.data) ? response.body.data : [])
          .filter((item) => {
            if (item.assetType !== assetType) return false
            const existingName = item.assetName || ''
            return selectedNames.some(
              (name) =>
                this.fileBaseName(name) === this.fileBaseName(existingName) ||
                name.toLowerCase() === String(existingName).toLowerCase()
            )
          })
          .map((item) => item.assetName)
        return Array.from(new Set(selectedDup.concat(existedDup)))
      },
      async submitVideoUpload() {
        const files = this.getRawFiles(this.videoFileList)
        if (!files.length) {
          this.$message.warning('请选择要上传的视频')
          return
        }
        const duplicates = await this.findDuplicateNames(files, ASSET_TYPE.VIDEO)
        if (duplicates.length) {
          this.$message.error(`视频名称重复，不允许上传：${duplicates.join('、')}`)
          return
        }
        this.videoUploading = true
        try {
          await uploadAndSubmitAssets({
            customerId: this.selectedCustomerId,
            assetType: ASSET_TYPE.VIDEO,
            files
          })
          this.$message.success('视频已提交上传')
          this.videoDialogVisible = false
          this.appliedFilterCustomerId = this.selectedCustomerId
          this.filterCustomerId = this.selectedCustomerId
          this.currentPage = 1
          await this.loadData()
        } catch (error) {
          this.$message.error(error.message || '视频上传失败')
        } finally {
          this.videoUploading = false
        }
      },
      async validateLogoFiles(files) {
        const allowedTypes = ['image/png', 'image/jpeg', 'image/gif']
        const errors = []
        for (const file of files) {
          const typeOk = allowedTypes.includes(file.type) || /\.(png|jpe?g|gif)$/i.test(file.name)
          if (!typeOk) {
            errors.push(`${file.name} 仅支持 GIF / JPEG / PNG`)
            continue
          }
          try {
            const size = await this.readImageSize(file)
            const ratio = size.width / size.height
            if (Math.abs(ratio - 1) > 0.01) {
              errors.push(`${file.name} 需为 1:1，当前 ${size.width}x${size.height}`)
            } else if (size.width < 128 || size.height < 128) {
              errors.push(`${file.name} 最低 128x128，当前 ${size.width}x${size.height}`)
            }
          } catch (error) {
            errors.push(`${file.name} 无法读取图片尺寸`)
          }
        }
        return errors
      },
      readImageSize(file) {
        return new Promise((resolve, reject) => {
          const url = URL.createObjectURL(file)
          const image = new Image()
          image.onload = () => {
            URL.revokeObjectURL(url)
            resolve({ width: image.width, height: image.height })
          }
          image.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error('read image failed'))
          }
          image.src = url
        })
      },
      async submitLogoUpload() {
        const files = this.getRawFiles(this.logoFileList)
        if (!files.length) {
          this.$message.warning('请选择要上传的徽标')
          return
        }
        const duplicates = await this.findDuplicateNames(files, ASSET_TYPE.LOGO)
        if (duplicates.length) {
          this.$message.error(`徽标名称重复，不允许上传：${duplicates.join('、')}`)
          return
        }
        const sizeErrors = await this.validateLogoFiles(files)
        if (sizeErrors.length) {
          this.$message.error(sizeErrors[0])
          return
        }
        this.logoUploading = true
        try {
          await uploadAndSubmitAssets({
            customerId: this.selectedCustomerId,
            assetType: ASSET_TYPE.LOGO,
            files
          })
          this.$message.success('徽标已提交上传')
          this.logoDialogVisible = false
          this.appliedFilterCustomerId = this.selectedCustomerId
          this.filterCustomerId = this.selectedCustomerId
          this.currentPage = 1
          await this.loadData()
        } catch (error) {
          this.$message.error(error.message || '徽标上传失败')
        } finally {
          this.logoUploading = false
        }
      },
      async handleExport() {
        this.exportLoading = true
        try {
          const result = await exportAssets({
            customerId: this.appliedFilterCustomerId
          })
          if (result.mode === 'empty') {
            await this.exportCurrentList()
          }
          this.$message.success('导出成功')
        } catch (error) {
          try {
            await this.exportCurrentList()
            this.$message.success('导出成功')
          } catch (fallbackError) {
            this.$message.error(error.message || '导出失败')
          }
        } finally {
          this.exportLoading = false
        }
      },
      async exportCurrentList() {
        const response = await listAssets({
          customerId: this.appliedFilterCustomerId,
          pageNum: 1,
          pageSize: 10000
        })
        const rows = (Array.isArray(response.body && response.body.data) ? response.body.data : []).map((item) => [
          item.assetId || item.id,
          item.customerId,
          this.getCustomerSite(item.customerId),
          item.uploadTime,
          this.mapUploadStatus(item.uploadStatus).label,
          item.adsName || '',
          this.mapAssetTypeLabel(item.assetType),
          item.assetName,
          item.assetResourceName || ''
        ])
        downloadExcel('Google素材列表.xlsx', [
          {
            name: '素材列表',
            header: ['素材ID', 'Customer ID', '站点', '上传时间', '上传状态', '广告系列名称', '素材类型', '视频名称', '实际使用名称'],
            rows
          }
        ])
      },
      handleSizeChange(val) {
        this.pageSize = val
        this.currentPage = 1
        this.loadData()
      },
      handleCurrentChange(val) {
        this.currentPage = val
        this.loadData()
      },
      setupPoll() {
        this.clearPoll()
        const hasPending = this.tableData.some(
          (item) => Number(item.uploadStatus) === UPLOAD_STATUS.UPLOADING
        )
        if (!hasPending) return
        this.pollTimer = setInterval(() => {
          this.loadData(true)
        }, 4000)
      },
      clearPoll() {
        if (this.pollTimer) {
          clearInterval(this.pollTimer)
          this.pollTimer = null
        }
      }
    }
  }
</script>

<style scoped>
  .google-page {
    min-height: calc(100vh - 60px);
    padding: 20px;
    background: #f5f7fa;
  }

  .header-card,
  .table-card {
    border-radius: 8px;
  }

  .page-header h2 {
    margin: 0 0 6px;
    font-size: 20px;
    font-weight: 600;
    color: #303133;
  }

  .page-header p {
    margin: 0;
    color: #909399;
    font-size: 13px;
    line-height: 1.6;
  }

  .customer-section {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
  }

  .section-label {
    margin-bottom: 10px;
    font-size: 13px;
    color: #606266;
  }

  .customer-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    min-height: 64px;
  }

  .customer-card {
    min-width: 180px;
    padding: 12px 16px;
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .customer-card:hover,
  .customer-card.active {
    border-color: #409eff;
    background: #ecf5ff;
  }

  .customer-id {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
  }

  .customer-card.active .customer-id,
  .customer-card.active .customer-site {
    color: #409eff;
  }

  .customer-site {
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
    word-break: break-all;
  }

  .empty-customers {
    color: #909399;
    font-size: 13px;
    line-height: 64px;
  }

  .header-actions {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-shrink: 0;
  }

  .upload-tip {
    margin-top: 16px;
  }

  .table-card {
    margin-top: 16px;
  }

  .table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .toolbar-left,
  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .table-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .total-text {
    color: #909399;
    font-size: 13px;
  }

  .pagination-container {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
  }

  .status-pill {
    display: inline-block;
    min-width: 64px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    border: 1px solid;
    line-height: 1.6;
  }

  .status-pill.is-success {
    color: #67c23a;
    background: #f0f9eb;
    border-color: #c2e7b0;
  }

  .status-pill.is-warning {
    color: #e6a23c;
    background: #fdf6ec;
    border-color: #f5dab1;
  }

  .status-pill.is-danger {
    color: #f56c6c;
    background: #fef0f0;
    border-color: #fbc4c4;
  }

  .status-pill.is-info {
    color: #909399;
    background: #f4f4f5;
    border-color: #d3d4d6;
  }

  @media (max-width: 768px) {
    .google-page {
      padding: 12px;
    }

    .customer-section {
      flex-direction: column;
    }

    .pagination-container {
      justify-content: center;
    }
  }
</style>
